// v3E.3 — Vaccinations page (list + add + bulk import + mark-done).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Syringe, CheckCircle2, AlertTriangle, Calendar, Sparkles, Trash2, ChevronRight, Loader2 } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";
import { useFamily } from "../contexts/FamilyContext";

const STATUS_META = {
    due:        { label: "Due today",   chip: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",  Icon: AlertTriangle },
    upcoming:   { label: "Upcoming",    chip: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300", Icon: Calendar },
    completed:  { label: "Completed",   chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300", Icon: CheckCircle2 },
    missed:     { label: "Missed",      chip: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300", Icon: AlertTriangle },
};

const EMPTY_FORM = { vaccine_name: "", dose_number: 1, due_date: "", notes: "", location: "" };

const AddForm = ({ familyMemberId, onSubmit, onCancel, submitting }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const submit = (e) => {
        e.preventDefault();
        onSubmit({ ...form, dose_number: Number(form.dose_number) || 1, family_member_id: familyMemberId || null });
    };
    return (
        <form onSubmit={submit} className="space-y-3 rounded-3xl border border-brand-100 bg-brand-50/40 p-5 dark:border-brand-400/20 dark:bg-brand-500/5" data-testid="vax-form">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Vaccine name</span>
                    <input name="vaccine_name" value={form.vaccine_name} onChange={onChange} required placeholder="e.g. Tetanus, MMR booster" className="input-clean mt-1" data-testid="vax-name" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Dose #</span>
                    <input name="dose_number" type="number" min="1" max="10" value={form.dose_number} onChange={onChange} className="input-clean mt-1" data-testid="vax-dose" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Due date</span>
                    <input name="due_date" type="date" value={form.due_date} onChange={onChange} required className="input-clean mt-1" data-testid="vax-due" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Location / clinic (optional)</span>
                    <input name="location" value={form.location} onChange={onChange} placeholder="e.g. Apollo Hospital" className="input-clean mt-1" data-testid="vax-location" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Notes (optional)</span>
                    <input name="notes" value={form.notes} onChange={onChange} placeholder="Anything else" className="input-clean mt-1" data-testid="vax-notes" />
                </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="vax-cancel">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="vax-save">{submitting ? "Saving..." : "Save vaccination"}</button>
            </div>
        </form>
    );
};

const ImportDialog = ({ open, onClose, onImport, busy }) => {
    const [scheduleType, setScheduleType] = useState("adult");
    const [dob, setDob] = useState("");
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[70]" data-testid="vax-import-modal">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="absolute inset-0 grid place-items-center p-4">
                <div className="w-full max-w-md rounded-3xl border border-zinc-100 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-zinc-900">
                    <p className="label-uppercase">Bulk import</p>
                    <h3 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Use a WHO-style template</h3>
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">We'll pre-fill all upcoming doses with realistic due dates. You can still edit each one.</p>
                    <div className="mt-5 space-y-3">
                        <label className="block">
                            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Schedule</span>
                            <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value)} className="input-clean mt-1" data-testid="vax-import-type">
                                <option value="adult">Adult set (annual flu, tetanus, boosters)</option>
                                <option value="child">Child / Infant (BCG → MMR → boosters)</option>
                            </select>
                        </label>
                        {scheduleType === "child" && (
                            <label className="block">
                                <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Child's date of birth</span>
                                <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="input-clean mt-1" required data-testid="vax-import-dob" />
                            </label>
                        )}
                    </div>
                    <div className="mt-6 flex justify-end gap-2">
                        <button onClick={onClose} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="vax-import-cancel">Cancel</button>
                        <button onClick={() => onImport({ schedule_type: scheduleType, dob: scheduleType === "child" ? dob : undefined })} disabled={busy || (scheduleType === "child" && !dob)} className="btn-brand !py-2 !px-5 !text-sm" data-testid="vax-import-go">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {busy ? "Importing..." : "Import schedule"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Vaccinations = () => {
    const { activeMember } = useFamily();
    const fmId = activeMember && !activeMember.is_self ? activeMember.id : null;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [adding, setAdding] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (fmId) params.family_member_id = fmId;
            const { data } = await api.get("/vaccinations", { params });
            setItems(data.items || []);
        } finally { setLoading(false); }
    }, [fmId]);

    useEffect(() => { load(); }, [load]);

    const onCreate = async (payload) => {
        try {
            setSubmitting(true);
            await api.post("/vaccinations", payload);
            toast.success("Vaccination added");
            setAdding(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally { setSubmitting(false); }
    };

    const onMarkDone = async (it) => {
        try {
            const today = new Date().toISOString().slice(0, 10);
            await api.patch(`/vaccinations/${it.id}`, { taken_date: today });
            toast.success("Marked as completed");
            await load();
        } catch { toast.error("Could not mark done"); }
    };

    const onDelete = async (it) => {
        if (!window.confirm("Delete this vaccination record?")) return;
        try {
            await api.delete(`/vaccinations/${it.id}`);
            toast.success("Deleted");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    const onImport = async ({ schedule_type, dob }) => {
        try {
            setImportBusy(true);
            const body = { schedule_type };
            if (dob) body.dob = dob;
            if (fmId) body.family_member_id = fmId;
            const { data } = await api.post("/vaccinations/bulk-import-template", body);
            toast.success(`Imported ${data.inserted} doses`);
            setImportOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not import");
        } finally { setImportBusy(false); }
    };

    const counts = useMemo(() => {
        const c = { all: items.length, due: 0, upcoming: 0, completed: 0, missed: 0 };
        items.forEach((v) => { c[v.status] = (c[v.status] || 0) + 1; });
        return c;
    }, [items]);

    const visible = filter === "all" ? items : items.filter((v) => v.status === filter);

    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
                <AuthGuard>
                    <header className="mb-6 flex flex-wrap items-end justify-between gap-3" data-testid="vaccinations-page">
                        <div>
                            <p className="label-uppercase">Vaccinations</p>
                            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
                                {activeMember?.is_self ? "Your immunisation record" : `${activeMember?.full_name || ""} · vaccinations`}
                            </h1>
                            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Track vaccines, get reminders before they're due, and keep one tidy record.</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-400/30 dark:bg-transparent dark:text-brand-300 dark:hover:bg-brand-500/10" data-testid="vax-import-btn">
                                <Sparkles className="h-4 w-4" /> Import schedule
                            </button>
                            <button onClick={() => setAdding(true)} className="btn-brand !py-2 !px-4 !text-sm" data-testid="vax-add-btn">
                                <Plus className="h-4 w-4" /> Add vaccination
                            </button>
                        </div>
                    </header>

                    <div className="mb-6 flex flex-wrap gap-2" data-testid="vax-filters">
                        {[
                            { v: "all", label: "All" },
                            { v: "due", label: "Due" },
                            { v: "upcoming", label: "Upcoming" },
                            { v: "completed", label: "Completed" },
                            { v: "missed", label: "Missed" },
                        ].map((f) => (
                            <button
                                key={f.v}
                                onClick={() => setFilter(f.v)}
                                data-testid={`vax-filter-${f.v}`}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition ${filter === f.v ? "bg-brand-700 text-white" : "border border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-300 dark:hover:bg-white/5"}`}
                            >
                                {f.label} <span className="ml-1 text-[10px] opacity-70">{counts[f.v] || 0}</span>
                            </button>
                        ))}
                    </div>

                    {adding && (
                        <div className="mb-6">
                            <AddForm familyMemberId={fmId} onSubmit={onCreate} onCancel={() => setAdding(false)} submitting={submitting} />
                        </div>
                    )}

                    {loading ? (
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {[1, 2, 3].map((i) => <div key={i} className="h-32 skeleton rounded-2xl" />)}
                        </div>
                    ) : visible.length === 0 ? (
                        <EmptyState
                            title={items.length === 0 ? "No vaccinations recorded yet" : `Nothing matches "${filter}"`}
                            description={items.length === 0 ? "Add a single shot, or import a starter schedule." : "Try another filter."}
                            action={items.length === 0 ? <button onClick={() => setImportOpen(true)} className="btn-brand"><Sparkles className="h-4 w-4" /> Import schedule</button> : null}
                        />
                    ) : (
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="vax-list">
                            {visible.map((v) => {
                                const meta = STATUS_META[v.status] || STATUS_META.upcoming;
                                const Icon = meta.Icon;
                                return (
                                    <div key={v.id} data-testid={`vax-card-${v.id}`} className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft transition hover:border-brand-200 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-brand-400/40">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                <span className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                                                    <Syringe className="h-5 w-5" />
                                                </span>
                                                <div>
                                                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{v.vaccine_name}</p>
                                                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">Dose {v.dose_number} · {v.due_date}</p>
                                                    {v.notes && <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{v.notes}</p>}
                                                </div>
                                            </div>
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.chip}`}>
                                                <Icon className="h-3 w-3" /> {meta.label}
                                            </span>
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {v.status !== "completed" && (
                                                <button onClick={() => onMarkDone(v)} data-testid={`vax-done-${v.id}`} className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25">
                                                    <CheckCircle2 className="mr-1 inline-block h-3 w-3" /> Mark done
                                                </button>
                                            )}
                                            <button onClick={() => onDelete(v)} data-testid={`vax-delete-${v.id}`} className="rounded-full border border-zinc-200 px-3 py-1 text-xs text-zinc-500 hover:border-rose-200 hover:text-rose-600 dark:border-white/10 dark:text-zinc-400 dark:hover:border-rose-400/30">
                                                <Trash2 className="mr-1 inline-block h-3 w-3" /> Delete
                                            </button>
                                            {v.taken_date && (
                                                <span className="rounded-full bg-zinc-50 px-3 py-1 text-xs text-zinc-500 dark:bg-white/[0.04] dark:text-zinc-400">Taken: {v.taken_date}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImport={onImport} busy={importBusy} />

                    <div className="mt-10 flex items-center justify-between rounded-3xl border border-zinc-100 bg-white p-5 shadow-soft dark:border-white/10 dark:bg-white/[0.03]" data-testid="vax-footer-links">
                        <div>
                            <p className="label-uppercase">More care tools</p>
                            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Appointments, lab reminders, emergency profile and more.</p>
                        </div>
                        <a href="/account" className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">Open Care hub <ChevronRight className="h-4 w-4" /></a>
                    </div>
                </AuthGuard>
            </section>
        </Layout>
    );
};

export default Vaccinations;
